import { useRef, useState } from 'react';

import { MinusCircleOutlined } from '@ant-design/icons';
import { BankCodesForAll } from '@revenuemarket/bank';
import { Flex, Input, Colors, BinLine, Button as ClobeButton, TextArea } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { Button, Form, FormInstance, InputRef, Typography } from 'antd';

import BusinessEntityFormList from '~components/BusinessEntity/BusinessEntityFormList';
import { AccountHolderStatus } from '~components/ui/AGGrid/renderers/AGGridAccountCheckHolderRenderer';
import { AddressModal } from '~components/ui/Modal/AddressModal';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useCheckAccountHolder } from '~hooks/queries/wiring/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { formatAddress, parseAddress } from '~utils/common/address';

import BankSelector from '../BankWiring/BankSelector';
import WiringAccountCheckBanner from '../BankWiring/WiringAccountCheckBanner';

const BusinessEntityDetailForm: React.FC<{
  form: FormInstance<any>;
}> = ({ form }) => {
  const { companyId } = useDemoAwareCompanyId();
  const { mutateAsync: checkAccountHolderRequest } = useCheckAccountHolder();

  const [accountCheckStatus, setAccountCheckStatus] = useState<AccountHolderStatus[]>([]);
  const [bankSelectorOpen, setBankSelectorOpen] = useState(false);
  const detailAddressInputRef = useRef<InputRef>(null);
  const values = Form.useWatch([], form);

  // 거래처 계좌 확인
  const checkBusinessEntityAccount = async (data: {
    organizationCode: BankCodesForAll;
    accountNumber: string;
    businessRegNo?: string;
    onSucess: (accountHolder: string) => void;
    onFail: () => void;
    index: number;
  }) => {
    const response = await checkAccountHolderRequest({
      companyId,
      request: {
        organizationCode: data.organizationCode,
        bankAccountNo: data.accountNumber,
        businessRegNo: data.businessRegNo,
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      data.onSucess(response.Data?.data?.holderName ?? '');
      setAccountCheckStatus((prev) => {
        const next = [...prev];
        next[data.index] = response.Data?.data?.identified ? 'CHECKED' : 'WARNING';
        return next;
      });
    } else {
      data.onFail();
      setAccountCheckStatus((prev) => {
        const next = [...prev];
        next[data.index] = 'FAILED';
        return next;
      });
    }
  };

  return (
    <Flex direction="column" gap={16} style={{ marginTop: 16 }}>
      <Flex direction="column" gap={16}>
        <Flex direction="column">
          <Flex gap={4} align="center">
            <Typography.Text style={{ color: Colors.gray800 }}>주소</Typography.Text>
          </Flex>
          <Form.Item
            name="address"
            required
            style={{
              marginBottom: 8,
            }}
          >
            <Input
              readOnly
              placeholder="주소"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                overlays.open(({ isOpen, onClose }) => (
                  <AddressModal
                    isOpen={isOpen}
                    onClose={onClose}
                    onComplete={(address) => {
                      form.setFieldsValue({
                        address: formatAddress(parseAddress(address)),
                      });

                      onClose();
                      // WARNING: antd delays recover focus to form item input
                      setTimeout(() => {
                        detailAddressInputRef.current?.focus();
                      }, 500);
                    }}
                  />
                ));
              }}
            />
          </Form.Item>
          <Form.Item name="lotAddress" noStyle>
            <Input ref={detailAddressInputRef} placeholder="상세 주소" />
          </Form.Item>
        </Flex>
        <Flex direction="column">
          <Flex gap={4} align="center">
            <Typography.Text style={{ color: Colors.gray800 }}>업태</Typography.Text>
          </Flex>

          <Form.Item name="btpNo" noStyle>
            <Input placeholder="" />
          </Form.Item>
        </Flex>
        <Flex direction="column">
          <Flex gap={4} align="center">
            <Typography.Text style={{ color: Colors.gray800 }}>종목</Typography.Text>
          </Flex>

          <Form.Item name="bzdNo" noStyle>
            <Input placeholder="" />
          </Form.Item>
        </Flex>
        <Flex direction="column">
          <Flex gap={4} align="center">
            <Typography.Text style={{ color: Colors.gray800 }}>메모</Typography.Text>
          </Flex>
          <Form.Item name="memo" noStyle>
            <TextArea placeholder="메모를 입력하세요" rows={4} />
          </Form.Item>
        </Flex>
        <BusinessEntityFormList
          title="세금계산서 담당자"
          addItemButtonText="담당자 추가"
          fieldName="taxInvoiceManagers"
          renderItems={(field, index, onRemove) => {
            // 에러 상태 확인
            const nameError = form.getFieldError(['taxInvoiceManagers', field.name, 'name']);
            const emailError = form.getFieldError(['taxInvoiceManagers', field.name, 'email']);

            return (
              <Flex key={field.key} gap={8} direction="column">
                <Flex gap={4}>
                  <Form.Item
                    name={[field.name, 'name']}
                    dependencies={[['taxInvoiceManagers', field.name, 'email']]}
                    rules={[
                      {
                        validator: async (_, value) => {
                          const name = (value ?? '').trim();
                          const email = (
                            form.getFieldValue(['taxInvoiceManagers', field.name, 'email']) ?? ''
                          ).trim();

                          // 둘 다 비어있으면 OK (에러 노출 X)
                          if (!name && !email) return Promise.resolve();

                          // email만 있으면 name 필수
                          if (!name && email)
                            return Promise.reject(new Error('이름을 입력해주세요'));

                          return Promise.resolve();
                        },
                      },
                    ]}
                    hasFeedback={false} // 기본 피드백 아이콘 숨기기
                  >
                    <Input
                      id={`taxInvoiceManagers_${field.name}_name`}
                      placeholder="이름"
                      inputStatus={nameError?.length > 0 ? 'error' : undefined}
                      onChange={(e) => {
                        // 입력 시작하면 해당 필드의 error 지우기
                        form.setFields([
                          {
                            name: ['taxInvoiceManagers', field.name, 'name'],
                            errors: [],
                          },
                        ]);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'email']}
                    dependencies={[['taxInvoiceManagers', field.name, 'name']]}
                    rules={[
                      {
                        validator: async (_, value) => {
                          const email = (value ?? '').trim();
                          const name = (
                            form.getFieldValue(['taxInvoiceManagers', field.name, 'name']) ?? ''
                          ).trim();

                          // 둘 다 비어있으면 OK
                          if (!name && !email) return Promise.resolve();

                          // name만 있으면 email 필수
                          if (name && !email)
                            return Promise.reject(new Error('이메일을 입력해주세요'));

                          // email 형식 검증 (공백만 입력한 경우는 empty로 간주)
                          if (!email) return Promise.resolve();

                          try {
                            await COMMON_YUP.email.validate(email);
                            return Promise.resolve();
                          } catch (error: any) {
                            return Promise.reject(new Error(error.message));
                          }
                        },
                      },
                    ]}
                    hasFeedback={false} // 기본 피드백 아이콘 숨기기
                  >
                    <Input
                      id={`taxInvoiceManagers_${field.name}_email`}
                      placeholder="이메일을 입력하세요"
                      width="100%"
                      inputStatus={emailError?.length > 0 ? 'error' : undefined}
                      onChange={(e) => {
                        // 입력 시작하면 해당 필드의 error 지우기
                        form.setFields([
                          {
                            name: ['taxInvoiceManagers', field.name, 'email'],
                            errors: [],
                          },
                        ]);
                      }}
                    />
                  </Form.Item>
                  <ClobeButton
                    buttonType="transparent"
                    onClick={() => onRemove?.(index)}
                    icon={<BinLine width={18} height={18} />}
                  ></ClobeButton>
                </Flex>
              </Flex>
            );
          }}
        />

        <BusinessEntityFormList
          title="거래처 입금 계좌"
          fieldName="bankAccounts"
          addItemButtonText="계좌 추가"
          renderItems={(field, index, onRemove) => (
            <Flex
              key={field.key}
              gap={4}
              style={{
                width: '100%',
              }}
            >
              <Form.Item name={[field.name, 'alias']} noStyle>
                <Input
                  placeholder="계좌 별칭"
                  style={{
                    width: 150,
                  }}
                />
              </Form.Item>
              <Form.Item name={[field.name, 'organizationCode']} noStyle>
                <BankSelector
                  onClick={(e) => {
                    if (bankSelectorOpen) {
                      e.stopPropagation();
                      return;
                    }
                    setBankSelectorOpen(true);
                  }}
                  open={bankSelectorOpen}
                  style={{
                    width: 150,
                  }}
                  className={css`
                    &&& .ant-select-selection-item {
                      display: flex;
                      align-items: center;
                    }
                  `}
                  placeholder="은행 선택"
                  bordered={true}
                  value={form.getFieldValue([field.name, 'organizationCode'])}
                  onChange={(value) => {
                    setBankSelectorOpen(false);
                    form.setFieldsValue({
                      [field.name]: {
                        ...form.getFieldValue(field.name),
                        organizationCode: value,
                      },
                    });
                  }}
                />
              </Form.Item>
              <Form.Item name={[field.name, 'accountNumber']} noStyle>
                <FormattedInput
                  style={{
                    width: 200,
                  }}
                  formatter={(value) => {
                    return (value as string).replace(/[^0-9]/g, '');
                  }}
                  deformatter={(value) => {
                    return value.replace(/[^0-9]/g, '');
                  }}
                  placeholder="계좌번호 (- 없이 숫자만 입력)"
                  onChange={(value) => {
                    form.setFieldsValue({ [field.name]: { accountNumber: value } });
                  }}
                />
              </Form.Item>

              <Button
                type="link"
                onClick={() => {
                  const target = values?.bankAccounts[index];
                  if (!target) return;

                  if (!target?.organizationCode || !target?.accountNumber) return;

                  checkBusinessEntityAccount({
                    organizationCode: target.organizationCode,
                    accountNumber: target.accountNumber,
                    businessRegNo: values.businessRegNumber,
                    onSucess: (accountHolder: string | null) => {
                      form.setFieldsValue({
                        bankAccounts: form
                          .getFieldValue('bankAccounts')
                          .map((item: any, idx: number) => {
                            if (idx === index) {
                              return {
                                ...item,
                                accountHolder,
                              };
                            }
                            return item;
                          }),
                      });
                    },
                    onFail: () => {
                      form.setFieldsValue({
                        bankAccounts: form
                          .getFieldValue('bankAccounts')
                          .map((item: any, idx: number) => {
                            if (idx === index) {
                              return {
                                ...item,
                                accountHolder: null,
                              };
                            }
                            return item;
                          }),
                      });
                    },
                    index,
                  });
                }}
              >
                예금주 확인
              </Button>
              <Form.Item name={[field.name, 'accountHolder']} noStyle hidden>
                <Input />
              </Form.Item>
              <Button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                type="ghost"
                onClick={() => {
                  onRemove?.(index);
                  setAccountCheckStatus((prev) => {
                    return prev.filter((_, idx) => idx !== index);
                  });
                }}
              >
                <MinusCircleOutlined />
              </Button>
            </Flex>
          )}
        />
      </Flex>

      {accountCheckStatus.includes('FAILED') && <WiringAccountCheckBanner status={'FAILED'} />}
      {accountCheckStatus.includes('WARNING') && <WiringAccountCheckBanner status={'WARNING'} />}
    </Flex>
  );
};

export default BusinessEntityDetailForm;
